<template>
  <!-- Description text wrapper including truncation and expand/contract options -->
  <div class="description-container" :class="{ rtl: rtl }">
    <!-- Glossary "windows" -->
    <!-- 
    Each word has one window, on click the matching window
    is displayed, positioned absolutely according to click
    -->
      <div
        class="glossary-entry-container"
        v-for="word in currentGlossary"
        :key="word.name"
        :ref="word.name"
        :dir="rtl ? 'rtl' : 'ltr'"
        >
        <div
            class="glossary-close"
            v-if="showGlossary[word.name]"
            @click="hideGlossary(word.name)"
        >
            <font-awesome-icon :icon="['fas', 'times']" />
        </div>
        <div
            class="glossary-header"
            v-if="showGlossary[word.name]"
        >
            Glossary
        </div>
        <div class="glossary-entry" v-if="showGlossary[word.name]">
            <div class="glossary-word">{{ currentWord }}</div>
            <div class="glossary-definition" v-html="word.definition"></div>
        </div>
      </div>
    <span class="description" v-html="display" ref="description"> </span>
    <span
      class="description-toggle"
      @click="toggleDescription(text, characters)"
      v-if="text.length > characters"
    >
      <span v-if="!full">Read more.</span>
      <span v-else>Read less.</span>
    </span>
  </div>
</template>

<script>
import truncate from "../mixins/truncate.js";
import language from "../mixins/language.js";
import media from "../mixins/media.js";
export default {
  name: "DescriptionComponent",
  metaInfo() {
    return {
      // Add meta information to improve search results
      meta: [
        {
          name: "description",
          content: this.text ? this.text : "",
        },
      ],
    };
  },
  props: {
    language: String,
    rtl: Boolean,
    texts: Array,
    characters: Number,
    glossary: Object,
  },
  mixins: [truncate, language, media],
  watch: {
    language() {
      this.full = false;
      this.text = this.returnValueInLanguage(
        this.language,
        this.texts
      ).description;
      this.display = this.truncate(this.characters, this.text);
      this.currentGlossary = this.glossary[this.language];
      this.glossaryActions();
    },
    texts() {
      this.full = false;
      this.text = this.returnValueInLanguage(
        this.language,
        this.texts
      ).description;
      this.display = this.truncate(this.characters, this.text);
      this.glossaryActions();
    },
    full() {
      this.glossaryActions();
    }
  },
  data() {
    return {
      full: false,
      text: "",
      display: "",
      currentGlossary: this.glossary ? this.glossary[this.language] : null,
			showGlossary: {},
			currentWord: "",
      lessThan1699: window.matchMedia("(max-width: 1699px)").matches,
    };
  },
  methods: {
    toggleDescription: function (text, chars) {
      if (this.full) {
        this.display = this.truncate(chars, text);
      } else {
        // Add space at the end so "Read less." has room
        this.display = text + " ";
      }
      this.full = !this.full;
    },
    glossaryActions: function () {
      if (this.currentGlossary) {
        // Set-up for in-description Glossary links
        // Short-hand variables for descriptions
        // List of glossary terms (one spelling)
        let words = [];
        // Array of objects, key = word, value = array of spellings
        let spellings = [];
        // Array of regexes created from spellings array
        let regexs = [];
        // Populate the showGlossary object
        // Every word gets assigned value for displaying
        // Initially false (all hidden), set to true on click
        for (let i = 0; i < this.currentGlossary.length; i++) {
          // Empty the showGlossary object (reset)
          // for (let j = 0; j < this.currentGlossary.length; j++) {
          // 	this.$delete(this.showGlossary, j);
          // }
          // this.$set(this.showGlossary, this.currentGlossary[i].name, false);
          this.showGlossary = {};
          this.showGlossary[this.currentGlossary[i].name] = false;
        }
        // Push the names/keys for each word into words array
        for (let i = 0; i < this.currentGlossary.length; i++) {
          words.push(this.currentGlossary[i].name);
          let obj = {};
          // Add nested array for every word key
          obj[this.currentGlossary[i].name] = [];
          // Push every spelling option into array
          // Remove extra whitespace characters
          for (
            let j = 0;
            j < this.currentGlossary[i].spellings.length;
            j++
          ) {
            let trimmed = this.currentGlossary[i].spellings[j].trim();
            obj[this.currentGlossary[i].name].push(trimmed);
          }
          // Format: [[ key: [key, ki, kee] ]]
          spellings.push(obj);
        }
        // Create array of regexs from spellings array
        for (let i = 0; i < words.length; i++) {
          let obj = {};
          // Key = word
          obj[words[i]] = [];
          // Get to array of spellings inside each word's object
          for (let j = 0; j < spellings[i][words[i]].length; j++) {
            // Change word boundaries for Arabic ([^،-٩])
            if (this.language === "ar") {
              let regex = new RegExp(
                `[^،-٩]${spellings[i][words[i]][j]}[^،-٩]`,
                "gi"
              );
              obj[words[i]].push(regex);
            } else {
              let regex = new RegExp(
                `\\b${spellings[i][words[i]][j]}\\b`,
                "gi"
              );
              obj[words[i]].push(regex);
            }
          }
          regexs.push(obj);
        }
        // Add anchor links to matching Glossary words in description(s)
        if (this.display) {
          this.display = this.searchAndWrap(
            this.display,
            words,
            regexs,
          );
        }
        // If Glossary data exists, then add click events
        // to show definition window when clicking a word
        // nextTick is required due to v-html, adding event listeners
        // after Vue has updated (using refs)
        if (this.currentGlossary) {
          this.$nextTick(() => {
            if (this.display) {
              this.addClicksToGlossary(
                this.$refs.description,
                words,
                spellings,
                regexs
              );
            }
          });
        }
      }
		},
		// Search provided text for regex, wrap matches in links
    searchAndWrap: function (text, words, regexs) {
      for (let i = 0; i < regexs.length; i++) {
        // Get to array of regexs inside each word's object
        for (let j = 0; j < regexs[i][words[i]].length; j++) {
          text = text.replace(regexs[i][words[i]][j], (match) => {
            return `<a class="glossary-link">${match}</a>`;
          });
        }
      }
      return text;
    },
		// Using refs, add click events to v-html text
    addClicksToGlossary: function (ref, words, spellings, regexs) {
      // Number of children (tags) inside description
      for (let i = 0; i < ref.children.length; i++) {
        for (let j = 0; j < regexs.length; j++) {
          // Get to array of regexs for each word
          for (let k = 0; k < regexs[j][words[j]].length; k++) {
            // Boolean for whether innerText of tags in description matches regex
            // Loop adds the correct click event to each instance of the matching word
            if (regexs[j][words[j]][k].test(ref.children[i].innerText)) {
              regexs[j][words[j]][k].lastIndex = 0;
              // If the element is not a.glossary-link
              // (Match triggered by i tag that contains a.glossary-link)
              if (ref.children[i].className !== "glossary-link") {
                // Check each child inside the i tag
                for (let l = 0; l < ref.children[i].children.length; l++) {
                  // Repeat of earlier test, but for inner children
                  if (
                    regexs[j][words[j]][k].test(
                      ref.children[i].children[l].innerText
                    )
                  ) {
                    regexs[j][words[j]][k].lastIndex = 0;
                    // Same code as below (refactor needed) but adds listener to the child
                    ref.children[i].children[l].addEventListener(
                      "click",
                      (e) => {
                        e.preventDefault();
                        let clickX = e.pageX;
                        let clickY = e.pageY;
                        // Larger widths: opens glossary window centered/above clicked area
                        // Smaller widths: opens glossary window in the middle of the page
                        // (top aligned with clicked area)
                        if (!this.lessThan1699) {
                          this.$refs[words[j]][0].style.top = `${clickY}px`;
                          this.$refs[words[j]][0].style.left = `${clickX}px`;
                          this.$refs[
                            words[j]
                          ][0].style.transform = `translate(-50%, -110%)`;
                        } else if (this.lessThan1699) {
                          this.$refs[words[j]][0].style.top = `${clickY}px`;
                          this.$refs[words[j]][0].style.left = "50%";
                          this.$refs[words[j]][0].style.transform =
                            "translateX(-50%)";
                        }
                        for (let m = 0; m < words.length; m++) {
                          this.showGlossary[words[m]] = false;
                        }
                        // Get the spelling of the word that matches the regex for displaying
                        this.currentWord = spellings[j][words[j]][k];
                        this.showGlossary[words[j]] = true;
                      }
                    );
                  }
                }
              } else {
                // If regex matches, add an event listener to open a glossary window
                ref.children[i].addEventListener("click", (e) => {
                  e.preventDefault();
                  let clickX = e.pageX;
                  let clickY = e.pageY;
                  if (!this.lessThan1699) {
                    this.$refs[words[j]][0].style.top = `${clickY}px`;
                    this.$refs[words[j]][0].style.left = `${clickX}px`;
                    this.$refs[
                      words[j]
                    ][0].style.transform = `translate(-50%, -110%)`;
                  } else if (this.lessThan1699) {
                    this.$refs[words[j]][0].style.top = `${clickY}px`;
                    this.$refs[words[j]][0].style.left = "50%";
                    this.$refs[words[j]][0].style.transform =
                      "translateX(-50%)";
                  }
                  for (let m = 0; m < words.length; m++) {
                    this.showGlossary[words[m]] = false;
                  }
                  // Get the spelling of the word that matches the regex for displaying
                  this.currentWord = spellings[j][words[j]][k];
                  this.showGlossary[words[j]] = true;
                });
              }
            }
          }
        }
      }
    },
    hideGlossary: function (word) {
      this.showGlossary[word] = false;
    },
  },
  mounted() {
    this.text = this.returnValueInLanguage(
      this.language,
      this.texts
    ).description;
    this.display = this.truncate(this.characters, this.text);
    this.glossaryActions();
  },
};
</script>

<style scoped lang="scss">
.description-container,
.description-container :deep(*) {
  @include texts;
  @include font-size-6;
}
.description-container :deep(a) {
  @include link-styles;
}
.description-container :deep(a:hover) {
  @include link-hover-styles;
}
.description-toggle {
  font-weight: bold;
  text-decoration: underline;
  cursor: pointer;
}

.rtl {
  direction: rtl;
}

.glossary-entry-container {
  width: 400px;
  position: absolute;
  z-index: 68;
  background-color: $medium-green;
  border-radius: 10px;
  color: white;
}
.glossary-close {
  @include flex-center;
  height: 30px;
  width: 30px;
  position: absolute;
  top: -10px;
  right: -15px;
  color: white;
  background-color: $dark-green;
  border-radius: 100%;
}
// Override size and padding for FA icon
.glossary-close .fa-times {
  color: white;
  font-size: 100%;
  padding-right: 0;
}
.glossary-header {
  @include flex-center;
  justify-content: flex-start;
  height: 60px;
  font-size: 110%;
  font-weight: bold;
  color: white;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: right;
  background-color: $medium-green;
  padding: 0 15px;
  border-radius: 10px 10px 0 0;
}
.glossary-entry {
  max-height: 150px;
  overflow: auto;
  position: relative;
  background-color: white;
  color: black;
  margin: 0 15px 15px 15px;
}
.glossary-word {
  @include flex-center;
  justify-content: flex-start;
  height: 40px;
  color: black;
  background-color: #f0ede4;
  padding: 0 15px;
}
.glossary-definition {
  padding: 15px;
}
:deep(.glossary-link) {
  @include link-styles;
  cursor: pointer;
}
:deep(.glossary-link:hover) {
  @include link-hover-styles;
}

@media only screen and (max-width: 499px) {
  .glossary-entry-container {
    width: 350px;
  }
}

@media only screen and (max-width: 399px) {
  .glossary-entry-container {
    width: 250px;
  }
}
</style>
